import type { IdentityErrorCode } from './IdentityErrorCode';

/** Per field detail of a serializer rejection, as it arrived. */
export type IdentityValidationErrors = Record<string, { code: string; message: string }[]>;

/**
 * Any auth failure that is not about the link, normalized so axios details stay in infrastructure.
 *
 * `status` is `0` when there was no response at all, which is how a lost connection is told apart
 * from a rejected request.
 *
 * It carries two codes because they answer two different questions: `code` decides what the user
 * reads, `apiCode` is what an engineer searches for in the backend logs. Collapsing them would force
 * a choice between a correct message and a correlatable log.
 */
export type IdentityApiError = Error & {
  readonly name: 'IdentityApiError';
  readonly code?: IdentityErrorCode;
  readonly apiCode?: string;
  readonly status: number;
  readonly retryAfterSeconds?: number;
  readonly validation?: IdentityValidationErrors | null;
};

/**
 * Creates a normalized IdentityApiError.
 *
 * It is identified by its `name` and not by a class, because class names are renamed in production
 * builds and `instanceof` stops working there.
 */
export const createIdentityApiError = (params: {
  message: string;
  status: number;
  code?: IdentityErrorCode;
  apiCode?: string;
  retryAfterSeconds?: number;
  validation?: IdentityValidationErrors | null;
}): IdentityApiError => {
  const error = new Error(params.message) as IdentityApiError;

  return Object.assign(error, {
    name: 'IdentityApiError' as const,
    code: params.code,
    apiCode: params.apiCode,
    status: params.status,
    retryAfterSeconds: params.retryAfterSeconds,
    validation: params.validation,
  });
};

/** Narrows an unknown value to IdentityApiError. Use it instead of `instanceof`. */
export const isIdentityApiError = (value: unknown): value is IdentityApiError =>
  value instanceof Error && (value as IdentityApiError).name === 'IdentityApiError';

/**
 * Whether the request never got an answer, which is the only failure the user can do something
 * about. `status` is `0` exactly then, so anything else — a 500, a code this build does not know, a
 * rejection that is not even one of these errors — is ours and must not be reported as their
 * connection.
 */
export const isOfflineFailure = (value: unknown): boolean => isIdentityApiError(value) && value.status === 0;
